// SPDX-License-Identifier: MIT
// Feature Annotations Redux Slice
// Stores structured annotations (label, tags, comment, author, custom properties)
// for drawn map features, plus a global pool of reusable tags.

// ============================================================================
// State Types
// ============================================================================

/** Structured annotation data for a single feature */
export interface FeatureAnnotation {
  label: string;
  tags: string[];
  comment: string;
  author: string;
  customProperties: Record<string, string>;
  /** ISO date string set when the annotation is saved (display only, not exported) */
  updatedAt?: string;
}

export interface FeatureAnnotationsState {
  /** Map from Kepler feature ID to structured annotation */
  annotations: Record<string, FeatureAnnotation>;
  /** Global pool of tags – any tag created on one feature becomes available on all */
  availableTags: string[];
  /** Whether the annotations panel is open */
  isPanelOpen: boolean;
  /** Currently selected feature ID in the annotations panel */
  selectedFeatureId: string | null;
}

/** Default annotation for a newly-annotated feature */
export function createEmptyAnnotation(): FeatureAnnotation {
  return { label: '', tags: [], comment: '', author: '', customProperties: {} };
}

// ============================================================================
// Action Types
// ============================================================================

export const FEATURE_ANNOTATIONS_ACTIONS = {
  TOGGLE_PANEL: '@featureAnnotations/TOGGLE_PANEL',
  SET_PANEL_OPEN: '@featureAnnotations/SET_PANEL_OPEN',
  SET_SELECTED_FEATURE: '@featureAnnotations/SET_SELECTED_FEATURE',
  SET_ANNOTATION: '@featureAnnotations/SET_ANNOTATION',
  SET_LABEL: '@featureAnnotations/SET_LABEL',
  SET_COMMENT: '@featureAnnotations/SET_COMMENT',
  SET_AUTHOR: '@featureAnnotations/SET_AUTHOR',
  ADD_TAG: '@featureAnnotations/ADD_TAG',
  REMOVE_TAG: '@featureAnnotations/REMOVE_TAG',
  ADD_AVAILABLE_TAG: '@featureAnnotations/ADD_AVAILABLE_TAG',
  DELETE_ANNOTATION: '@featureAnnotations/DELETE_ANNOTATION',
  DELETE_ALL_FEATURE_ANNOTATIONS: '@featureAnnotations/DELETE_ALL_FEATURE_ANNOTATIONS',
  CLEAR_ALL: '@featureAnnotations/CLEAR_ALL',
  /** Dispatched by AnnotationsPanelTab before performing a GeoJSON export.
   *  The exportControlMiddleware intercepts this action and blocks it when
   *  secure datasets are loaded, providing centralised enforcement. */
  EXPORT_DRAWN_FEATURES: '@featureAnnotations/EXPORT_DRAWN_FEATURES',
  /** Bulk-restores annotations parsed from an imported annotated GeoJSON file. */
  LOAD_ANNOTATIONS_FROM_GEOJSON: '@featureAnnotations/LOAD_ANNOTATIONS_FROM_GEOJSON',
  /** Atomically saves all fields for a single feature annotation (staged edit). */
  SAVE_ANNOTATION_DRAFT: '@featureAnnotations/SAVE_ANNOTATION_DRAFT',
} as const;

// ============================================================================
// Action Interfaces
// ============================================================================

interface TogglePanelAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.TOGGLE_PANEL;
}

interface SetPanelOpenAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.SET_PANEL_OPEN;
  payload: boolean;
}

interface SetSelectedFeatureAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.SET_SELECTED_FEATURE;
  payload: string | null;
}

interface SetAnnotationAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.SET_ANNOTATION;
  payload: { featureId: string; key: string; value: string };
}

interface SetLabelAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.SET_LABEL;
  payload: { featureId: string; label: string };
}

interface SetCommentAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.SET_COMMENT;
  payload: { featureId: string; comment: string };
}

interface SetAuthorAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.SET_AUTHOR;
  payload: { featureId: string; author: string };
}

interface AddTagAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.ADD_TAG;
  payload: { featureId: string; tag: string };
}

interface RemoveTagAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.REMOVE_TAG;
  payload: { featureId: string; tag: string };
}

interface AddAvailableTagAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.ADD_AVAILABLE_TAG;
  payload: string;
}

interface DeleteAnnotationAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.DELETE_ANNOTATION;
  payload: { featureId: string; key: string };
}

interface DeleteAllFeatureAnnotationsAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.DELETE_ALL_FEATURE_ANNOTATIONS;
  payload: string; // featureId
}

interface ClearAllAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.CLEAR_ALL;
}

interface ExportDrawnFeaturesAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.EXPORT_DRAWN_FEATURES;
}

interface LoadAnnotationsFromGeoJSONAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.LOAD_ANNOTATIONS_FROM_GEOJSON;
  payload: {
    annotations: Record<string, FeatureAnnotation>;
    availableTags: string[];
  };
}

interface SaveAnnotationDraftAction {
  type: typeof FEATURE_ANNOTATIONS_ACTIONS.SAVE_ANNOTATION_DRAFT;
  payload: {
    featureId: string;
    annotation: Omit<FeatureAnnotation, 'updatedAt'>;
  };
}

export type FeatureAnnotationsAction =
  | TogglePanelAction
  | SetPanelOpenAction
  | SetSelectedFeatureAction
  | SetAnnotationAction
  | SetLabelAction
  | SetCommentAction
  | SetAuthorAction
  | AddTagAction
  | RemoveTagAction
  | AddAvailableTagAction
  | DeleteAnnotationAction
  | DeleteAllFeatureAnnotationsAction
  | ClearAllAction
  | ExportDrawnFeaturesAction
  | LoadAnnotationsFromGeoJSONAction
  | SaveAnnotationDraftAction;

// ============================================================================
// Action Creators
// ============================================================================

export const toggleAnnotationsPanel = (): TogglePanelAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.TOGGLE_PANEL,
});

export const setAnnotationsPanelOpen = (isOpen: boolean): SetPanelOpenAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.SET_PANEL_OPEN,
  payload: isOpen,
});

export const setSelectedFeature = (featureId: string | null): SetSelectedFeatureAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.SET_SELECTED_FEATURE,
  payload: featureId,
});

export const setAnnotation = (
  featureId: string,
  key: string,
  value: string
): SetAnnotationAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.SET_ANNOTATION,
  payload: { featureId, key, value },
});

export const setLabel = (featureId: string, label: string): SetLabelAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.SET_LABEL,
  payload: { featureId, label },
});

export const setComment = (featureId: string, comment: string): SetCommentAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.SET_COMMENT,
  payload: { featureId, comment },
});

export const setAuthor = (featureId: string, author: string): SetAuthorAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.SET_AUTHOR,
  payload: { featureId, author },
});

export const addTag = (featureId: string, tag: string): AddTagAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.ADD_TAG,
  payload: { featureId, tag },
});

export const removeTag = (featureId: string, tag: string): RemoveTagAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.REMOVE_TAG,
  payload: { featureId, tag },
});

export const addAvailableTag = (tag: string): AddAvailableTagAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.ADD_AVAILABLE_TAG,
  payload: tag,
});

export const deleteAnnotation = (featureId: string, key: string): DeleteAnnotationAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.DELETE_ANNOTATION,
  payload: { featureId, key },
});

export const deleteAllFeatureAnnotations = (
  featureId: string
): DeleteAllFeatureAnnotationsAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.DELETE_ALL_FEATURE_ANNOTATIONS,
  payload: featureId,
});

export const clearAllAnnotations = (): ClearAllAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.CLEAR_ALL,
});

export const exportDrawnFeatures = (): ExportDrawnFeaturesAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.EXPORT_DRAWN_FEATURES,
});

export const loadAnnotationsFromGeoJSON = (
  annotations: Record<string, FeatureAnnotation>,
  availableTags: string[]
): LoadAnnotationsFromGeoJSONAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.LOAD_ANNOTATIONS_FROM_GEOJSON,
  payload: { annotations, availableTags },
});

/**
 * Atomically save all fields for a single feature annotation.
 * Sets `updatedAt` to the current timestamp.
 * Used by the two-state annotation editor's "Save Feature" button.
 */
export const saveAnnotationDraft = (
  featureId: string,
  annotation: Omit<FeatureAnnotation, 'updatedAt'>
): SaveAnnotationDraftAction => ({
  type: FEATURE_ANNOTATIONS_ACTIONS.SAVE_ANNOTATION_DRAFT,
  payload: { featureId, annotation },
});

// ============================================================================
// Helpers
// ============================================================================

/** Ensure a feature has an annotation entry, creating a default if missing. */
function ensureAnnotation(
  annotations: Record<string, FeatureAnnotation>,
  featureId: string
): FeatureAnnotation {
  return annotations[featureId] || createEmptyAnnotation();
}

// ============================================================================
// Initial State
// ============================================================================

const INITIAL_STATE: FeatureAnnotationsState = {
  annotations: {},
  availableTags: [],
  isPanelOpen: false,
  selectedFeatureId: null,
};

// ============================================================================
// Reducer
// ============================================================================

export function featureAnnotationsReducer(
  state: FeatureAnnotationsState = INITIAL_STATE,
  action: FeatureAnnotationsAction
): FeatureAnnotationsState {
  switch (action.type) {
    case FEATURE_ANNOTATIONS_ACTIONS.TOGGLE_PANEL:
      return { ...state, isPanelOpen: !state.isPanelOpen };

    case FEATURE_ANNOTATIONS_ACTIONS.SET_PANEL_OPEN:
      return { ...state, isPanelOpen: action.payload };

    case FEATURE_ANNOTATIONS_ACTIONS.SET_SELECTED_FEATURE:
      return { ...state, selectedFeatureId: action.payload };

    case FEATURE_ANNOTATIONS_ACTIONS.SET_LABEL: {
      const { featureId, label } = action.payload;
      const current = ensureAnnotation(state.annotations, featureId);
      return {
        ...state,
        annotations: { ...state.annotations, [featureId]: { ...current, label } },
      };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.SET_COMMENT: {
      const { featureId, comment } = action.payload;
      const current = ensureAnnotation(state.annotations, featureId);
      return {
        ...state,
        annotations: { ...state.annotations, [featureId]: { ...current, comment } },
      };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.SET_AUTHOR: {
      const { featureId, author } = action.payload;
      const current = ensureAnnotation(state.annotations, featureId);
      return {
        ...state,
        annotations: { ...state.annotations, [featureId]: { ...current, author } },
      };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.ADD_TAG: {
      const { featureId, tag } = action.payload;
      const current = ensureAnnotation(state.annotations, featureId);
      if (current.tags.includes(tag)) return state;
      const newAnnotations = {
        ...state.annotations,
        [featureId]: { ...current, tags: [...current.tags, tag] },
      };
      // Also add to global pool if not present
      const newAvailable = state.availableTags.includes(tag)
        ? state.availableTags
        : [...state.availableTags, tag];
      return { ...state, annotations: newAnnotations, availableTags: newAvailable };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.REMOVE_TAG: {
      const { featureId, tag } = action.payload;
      const current = ensureAnnotation(state.annotations, featureId);
      return {
        ...state,
        annotations: {
          ...state.annotations,
          [featureId]: { ...current, tags: current.tags.filter(t => t !== tag) },
        },
      };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.ADD_AVAILABLE_TAG: {
      const tag = action.payload;
      if (state.availableTags.includes(tag)) return state;
      return { ...state, availableTags: [...state.availableTags, tag] };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.SET_ANNOTATION: {
      const { featureId, key, value } = action.payload;
      const current = ensureAnnotation(state.annotations, featureId);
      return {
        ...state,
        annotations: {
          ...state.annotations,
          [featureId]: {
            ...current,
            customProperties: { ...current.customProperties, [key]: value },
          },
        },
      };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.DELETE_ANNOTATION: {
      const { featureId, key } = action.payload;
      const current = ensureAnnotation(state.annotations, featureId);
      const updated = { ...current.customProperties };
      delete updated[key];
      return {
        ...state,
        annotations: {
          ...state.annotations,
          [featureId]: { ...current, customProperties: updated },
        },
      };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.DELETE_ALL_FEATURE_ANNOTATIONS: {
      const updated = { ...state.annotations };
      delete updated[action.payload];
      return { ...state, annotations: updated };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.CLEAR_ALL:
      return { ...state, annotations: {} };

    case FEATURE_ANNOTATIONS_ACTIONS.LOAD_ANNOTATIONS_FROM_GEOJSON: {
      const { annotations: incoming, availableTags: incomingTags } = action.payload;
      // Merge incoming annotations over existing ones (incoming wins on conflict)
      const merged = { ...state.annotations, ...incoming };
      // Merge available tags (union of existing and imported)
      const tagSet = new Set([...state.availableTags, ...incomingTags]);
      return { ...state, annotations: merged, availableTags: [...tagSet] };
    }

    case FEATURE_ANNOTATIONS_ACTIONS.SAVE_ANNOTATION_DRAFT: {
      const { featureId, annotation } = action.payload;
      // Merge the draft's tags into availableTags
      const newTagSet = new Set([...state.availableTags, ...annotation.tags]);
      return {
        ...state,
        annotations: {
          ...state.annotations,
          [featureId]: { ...annotation, updatedAt: new Date().toISOString() },
        },
        availableTags: [...newTagSet],
      };
    }

    default:
      return state;
  }
}

// ============================================================================
// Selectors
// ============================================================================

export interface RootStateWithAnnotations {
  featureAnnotations: FeatureAnnotationsState;
}

export const selectAnnotationsState = (state: RootStateWithAnnotations): FeatureAnnotationsState =>
  state.featureAnnotations;

export const selectIsAnnotationsPanelOpen = (state: RootStateWithAnnotations): boolean =>
  state.featureAnnotations.isPanelOpen;

export const selectSelectedFeatureId = (state: RootStateWithAnnotations): string | null =>
  state.featureAnnotations.selectedFeatureId;

export const selectAllAnnotations = (
  state: RootStateWithAnnotations
): Record<string, FeatureAnnotation> => state.featureAnnotations.annotations;

export const selectFeatureAnnotations =
  (featureId: string) =>
  (state: RootStateWithAnnotations): FeatureAnnotation =>
    state.featureAnnotations.annotations[featureId] || createEmptyAnnotation();

export const selectAvailableTags = (state: RootStateWithAnnotations): string[] =>
  state.featureAnnotations.availableTags;
